import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import dateSort from 'scaleflow/utils/dateSort';
import generatePositionFromTime from 'scaleflow/utils/generate-position-from-time';

export default class InitiativesPopoverProgressIndexComponent extends Component {
  @service intl;
  @service store;
  @service currentUser;
  @service initiativeSlideover;
  @service betaToggle;

  @tracked activeTab = 'all';
  @tracked creatingNew = false;
  get tabs() {
    return [
      {
        label: `${this.intl.t('dictionary.all')} ${this.intl.t(
          '_initiatives.update',
          {
            num: 2,
          },
        )}`,
        key: 'all',
        isVisibleToBetaUsers: true,
      },
      {
        label: this.intl.t('dictionary.uncertainty', {
          num: 2,
        }),
        key: 'uncertainty',
        count: this.initiative?.unarchivedUncertaintiesDetailed?.open ?? 0,
        isVisibleToBetaUsers: true,
      },
      {
        label: this.intl.t('dictionary.waiting-for-answers'),
        key: 'waiting',
        count: this.initiative?.unarchivedWfasDetailed?.open ?? 0,
        isVisibleToBetaUsers: true,
      },
      {
        label: this.intl.t('dictionary.metrics', { num: 2 }),
        key: 'metrics',
        isVisibleToBetaUsers: this.betaToggle.showMetrics,
      },
    ];
  }

  get uncertainties() {
    return this.initiative?.uncertainties?.content ?? [];
  }

  get wfas() {
    return this.initiative?.wfas?.content ?? [];
  }

  get initiativeUpdates() {
    return this.initiative?.['initiative-updates']?.content ?? [];
  }

  @action
  setActiveTab(tab) {
    this.activeTab = tab.key;
  }

  get initiative() {
    return this.args.initiative;
  }

  allItemsSortedByDate() {
    const uncertainties = this.uncertainties;
    const wfas = this.wfas;
    const updates = this.initiativeUpdates;
    const allItems = [...uncertainties, ...wfas, ...updates];
    allItems.sort((a, b) => dateSort(b.created_at, a.created_at));
    return allItems;
  }

  get items() {
    switch (this.activeTab) {
      case 'all':
        return this.allItemsSortedByDate();
      case 'uncertainty':
        return this.uncertainties.toSorted((a, b) =>
          dateSort(b.created_at, a.created_at),
        );
      case 'waiting':
        return this.wfas.toSorted((a, b) =>
          dateSort(b.created_at, a.created_at),
        );
      case 'metrics':
        return this.initiative.metrics;
      default:
        return [];
    }
  }

  setActiveTabToAll() {
    this.setActiveTab({ key: 'all' });
  }

  @action
  createNewUncertainty() {
    this.setActiveTabToAll();
    const uncertainty = this.store.createRecord('uncertainty');
    uncertainty.author = this.currentUser.currentUser;
    uncertainty.assignees = [this.currentUser.currentUser];
    uncertainty.initiative = this.initiative;
    uncertainty.position = generatePositionFromTime();
    this.creatingNew = true;
  }

  @action
  createNewWaiting() {
    this.setActiveTabToAll();
    const wfa = this.store.createRecord('wfa');
    wfa.author = this.currentUser.currentUser;
    wfa.assignees = [this.currentUser.currentUser];
    wfa.initiative = this.initiative;
    wfa.position = generatePositionFromTime();
    this.creatingNew = true;
  }

  @action
  createNewUpdate() {
    this.setActiveTabToAll();
    const update = this.store.createRecord('initiative-update');
    update.initiative = this.initiative;
    update.initiative_id = this.initiative.id;
    update.created_at = new Date().toISOString();
    update.updated_by = { type: 'PERSON', id: this.currentUser.id };
    this.creatingNew = true;
  }

  @action
  deactivateCreatingNew() {
    this.creatingNew = false;
  }
}
